import React from 'react';

const GameResults = ({ results, game, onPlayAgain, onShowLeaderboard, onBackToGames }) => {
  const getPerformanceMessage = (score) => {
    if (score >= 90) return { message: "Outstanding! 🏆", color: "text-yellow-600", emoji: "🌟" };
    if (score >= 80) return { message: "Excellent! 🎉", color: "text-green-600", emoji: "🎯" };
    if (score >= 70) return { message: "Great Job! 👏", color: "text-blue-600", emoji: "💪" };
    if (score >= 60) return { message: "Good Work! 👍", color: "text-purple-600", emoji: "📈" };
    if (score >= 50) return { message: "Keep Practicing! 💪", color: "text-orange-600", emoji: "🎯" };
    return { message: "Keep Trying! 🌱", color: "text-gray-600", emoji: "🚀" };
  };

  const getGameIcon = (gameType) => {
    switch (gameType) {
      case 'speed_arithmetic': return '⚡';
      case 'pattern_recognition': return '🧩';
      case 'math_battle': return '⚔️';
      case 'digital_abacus': return '🧮';
      default: return '🎮';
    }
  };

  const getGameColor = (gameType) => {
    switch (gameType) {
      case 'speed_arithmetic': return 'from-orange-400 to-red-500';
      case 'pattern_recognition': return 'from-purple-400 to-indigo-500';
      case 'math_battle': return 'from-red-400 to-pink-500';
      case 'digital_abacus': return 'from-teal-400 to-cyan-500';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const performance = getPerformanceMessage(results.score);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-r ${getGameColor(game?.game_type)} p-8 text-white text-center`}>
          <div className="text-5xl mb-4">{getGameIcon(game?.game_type)}</div>
          <h1 className="text-3xl font-bold mb-2">Game Complete!</h1>
          <p className="text-white/90">{game?.name}</p>
        </div>

        {/* Results */}
        <div className="p-8">
          {/* Performance Badge */}
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">{performance.emoji}</div>
            <h2 className={`text-3xl font-bold ${performance.color} mb-2`}>
              {performance.message}
            </h2>
            <p className="text-gray-600">
              You scored {results.score}% accuracy!
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            {/* Score */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-blue-600 mb-1">
                {results.score}%
              </div>
              <div className="text-sm text-blue-800">Accuracy</div>
            </div>

            {/* Correct Answers */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {results.correctAnswers}
              </div>
              <div className="text-sm text-green-800">Correct</div>
            </div>

            {/* Questions Attempted */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-purple-600 mb-1">
                {results.questionsAttempted}
              </div>
              <div className="text-sm text-purple-800">Attempted</div>
            </div>

            {/* Time Taken */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-orange-600 mb-1">
                {formatTime(results.timeTaken)}
              </div>
              <div className="text-sm text-orange-800">Time</div>
            </div>
          </div>

          {/* Points Earned */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-xl p-6 mb-8 text-center">
            <div className="flex items-center justify-center">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/>
                </svg>
              </div>
              <div>
                <p className="text-sm text-orange-600 font-medium">XP Points Earned</p>
                <p className="text-3xl font-bold text-orange-700">+{results.pointsEarned || results.correctAnswers}</p>
              </div>
            </div>
          </div>

          {/* Performance Breakdown */}
          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <h3 className="font-bold text-gray-900 mb-4 text-center">Performance Breakdown</h3>
            
            {/* Accuracy Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Accuracy</span>
                <span>{results.score}%</span>
              </div>
              <div className="bg-gray-200 rounded-full h-3">
                <div 
                  className={`bg-gradient-to-r ${getGameColor(game?.game_type)} h-3 rounded-full transition-all duration-1000`}
                  style={{ width: `${results.score}%` }}
                ></div>
              </div>
            </div>

            {/* Additional Stats */}
            <div className="text-sm text-gray-600 space-y-1">
              <div className="flex justify-between">
                <span>Questions per minute:</span>
                <span className="font-medium">
                  {results.timeTaken > 0 ? Math.round((results.questionsAttempted / results.timeTaken) * 60) : 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Average time per question:</span>
                <span className="font-medium">
                  {results.questionsAttempted > 0 ? Math.round(results.timeTaken / results.questionsAttempted) : 0}s
                </span>
              </div>
            </div>
          </div>

          {/* Achievements */}
          {results.score >= 80 && (
            <div className="bg-gradient-to-r from-yellow-100 to-orange-100 border border-yellow-300 rounded-xl p-4 mb-8">
              <div className="flex items-center justify-center">
                <span className="text-2xl mr-3">🏆</span>
                <div className="text-center">
                  <p className="font-bold text-yellow-800">Achievement Unlocked!</p>
                  <p className="text-sm text-yellow-700">
                    {results.score >= 95 ? 'Perfect Score Master!' : 
                     results.score >= 90 ? 'Math Wizard!' : 'Accuracy Expert!'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={onPlayAgain}
              className={`bg-gradient-to-r ${getGameColor(game?.game_type)} text-white py-3 px-4 rounded-lg font-medium hover:opacity-90 transition-all duration-200 transform hover:scale-105`}
            >
              Play Again
            </button>
            
            <button
              onClick={onBackToGames}
              className="bg-white hover:bg-gray-50 text-gray-600 py-3 px-4 rounded-lg font-medium border border-gray-300 transition-colors"
            >
              Back to Games
            </button>
          </div>

          {/* Tips for Improvement */}
          {results.score < 80 && (
            <div className="mt-8 bg-blue-50 rounded-xl p-6">
              <h3 className="font-bold text-blue-900 mb-3 text-center">💡 Tips to Improve</h3>
              <div className="space-y-2 text-sm text-blue-800">
                {game?.game_type === 'speed_arithmetic' && (
                  <>
                    <p>• Focus on accuracy first, then build up speed</p>
                    <p>• Practice mental math shortcuts like doubling and halving</p>
                    <p>• Learn multiplication tables by heart</p>
                  </>
                )}
                {game?.game_type === 'pattern_recognition' && (
                  <>
                    <p>• Look for common patterns: +n, ×n, squares, fibonacci</p>
                    <p>• Check the difference between consecutive numbers</p>
                    <p>• Consider alternating patterns or two interlaced sequences</p>
                  </>
                )}
                {game?.game_type === 'digital_abacus' && (
                  <>
                    <p>• Start with small numbers and work up to larger ones</p>
                    <p>• Remember: top bead = 5, each bottom bead = 1</p>
                    <p>• Practice visualizing the abacus in your head</p>
                  </>
                )}
                <p>• Take your time to understand the pattern before answering</p>
                <p>• Practice regularly to improve pattern recognition skills</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameResults;